import * as React from 'react';
import { createPortal } from 'react-dom';
import { useAction, useConvex, useQuery } from 'convex/react';
import { Building2, Loader2 } from 'lucide-react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import type { Locale } from '@bill-alps/backend/shared/format/locales';
import { normalizeIde } from '@bill-alps/backend/features/companies/ide/format';
import type {
  ZefixCompanyPrefill,
  ZefixSuggestion,
} from '@bill-alps/backend/features/zefix/models';
import { Badge, cn, Input } from '@bill-alps/ui';

import { useLanguage, useTranslation } from '../i18n/useTranslation';
import { zefixErrorKey } from '@/lib/errors/zefix-errors';

const DEBOUNCE_MS = 700;
const MIN_QUERY_LENGTH = 3;
const INLINE_RESULTS_STYLE: React.CSSProperties = {
  position: 'static',
  width: '100%',
  visibility: 'visible',
};

/** A company already on Bill Alps, matched by UID. */
export interface ExistingCompanyOption {
  companyId: string;
  name: string;
  ide: string;
  owned: boolean;
}

type SearchOption =
  | { kind: 'existing'; key: string; existing: ExistingCompanyOption }
  | { kind: 'register'; key: string; suggestion: ZefixSuggestion };

export interface ZefixCompanySearchProps {
  id?: string;
  /** Always the company name — the widget never owns a separate text state. */
  value: string;
  /** Free typing keeps working: every keystroke is forwarded verbatim. */
  onValueChange: (name: string) => void;
  /** A register hit, or null when the user typed over an earlier pick. */
  onSelect: (prefill: ZefixCompanyPrefill | null) => void;
  /**
   * Cross the register hits with the companies already on Bill Alps and
   * show those first, under their own heading. A pick there carries
   * `existingCompany` and costs no register quota. Mandate creation only.
   */
  detectExisting?: boolean;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** Render the dropdown inline instead of portaling. Use inside dialogs. */
  disablePortal?: boolean;
  /** Keep the dropdown in normal document flow so it cannot cover dialog actions. */
  inlineResults?: boolean;
  className?: string;
}

/**
 * Type-ahead over the federal commercial register (Zefix), used to prefill
 * name / IDE / address / canton.
 *
 * App-level rather than `packages/ui` on purpose: Zefix needs HTTP Basic auth,
 * so the calls go through Convex actions (`useAction`), and `packages/ui`
 * carries no Convex dependency. `SwissAddressInput` lives there only because
 * GeoAdmin is unauthenticated.
 *
 * The register is a convenience, never a gate. Out of quota, an outage, a
 * company that is not registered — in every case the input stays an ordinary
 * text field and the message is muted, not an error.
 *
 * With `detectExisting`, the results are split in two groups: companies
 * already on Bill Alps (matched on the UID of each register hit, or on a
 * UID typed directly — which needs no register call at all) and the plain
 * register hits.
 */
export function ZefixCompanySearch({
  id,
  value,
  onValueChange,
  onSelect,
  detectExisting = false,
  disabled = false,
  error = false,
  placeholder,
  autoFocus,
  disablePortal = false,
  inlineResults = false,
  className,
}: ZefixCompanySearchProps) {
  const { t } = useTranslation('zefix');
  const { language: locale } = useLanguage();
  const convex = useConvex();
  const quota = useQuery(api.features.zefix.registry.myQuota, {});
  const searchCompanies = useAction(
    api.features.zefix.registry.searchCompanies,
  );
  const getCompanyDetails = useAction(
    api.features.zefix.registry.getCompanyDetails,
  );

  const [options, setOptions] = React.useState<SearchOption[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [messageKey, setMessageKey] = React.useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // Monotonic id of the in-flight search: a Convex action cannot be aborted,
  // so a late answer is discarded by comparing against the latest request.
  const requestRef = React.useRef(0);
  const listboxId = React.useId();
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>(
    { visibility: 'hidden' },
  );

  const outOfSearches = quota?.searchesRemaining === 0;

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keep the highlighted option in view
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-option-index="${highlightedIndex}"]`,
    ) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  // Position the dropdown (layout effect to avoid a flash at the wrong spot)
  React.useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    if (inlineResults) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gap = 8;
    const dropdownMaxHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeAbove =
      spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setDropdownStyle(
      disablePortal
        ? {
            position: 'absolute',
            ...(placeAbove
              ? { bottom: '100%', marginBottom: gap }
              : { top: '100%', marginTop: gap }),
            left: 0,
            width: '100%',
            zIndex: 9999,
          }
        : {
            position: 'fixed',
            ...(placeAbove
              ? { bottom: window.innerHeight - rect.top + gap }
              : { top: rect.bottom + gap }),
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          },
    );
  }, [open, disablePortal, inlineResults, options.length, messageKey]);

  React.useEffect(() => {
    return () => {
      // Nothing in flight may touch state after unmount: bump the request id
      // so any pending answer is ignored, and drop the pending debounce.
      requestRef.current += 1;
      clearTimeout(debounceRef.current);
    };
  }, []);

  /**
   * Which of these UIDs are already on Bill Alps. One-shot query rather than
   * a subscription: the set changes on every keystroke. Failures degrade to
   * "nothing known" — the register hits still show.
   */
  const lookupExisting = React.useCallback(
    async (ides: string[]): Promise<Map<string, ExistingCompanyOption>> => {
      const out = new Map<string, ExistingCompanyOption>();
      if (!detectExisting || ides.length === 0) return out;
      try {
        const rows = await convex.query(
          api.features.fiduciaire.mandateRequests.lookupByIdes,
          { ides },
        );
        for (const row of rows) {
          out.set(row.ideNormalized, {
            companyId: row.companyId,
            name: row.name,
            ide:
              ides.find((ide) => normalizeIde(ide) === row.ideNormalized) ??
              row.ideNormalized,
            owned: row.owned,
          });
        }
      } catch {
        // Not knowing is fine: the register hits still show.
      }
      return out;
    },
    [convex, detectExisting],
  );

  /** Register hits, existing ones split out first. */
  const buildOptions = React.useCallback(
    (
      suggestions: ZefixSuggestion[],
      existing: Map<string, ExistingCompanyOption>,
    ): SearchOption[] => {
      const found: SearchOption[] = [];
      const register: SearchOption[] = [];
      const seen = new Set<string>();
      for (const suggestion of suggestions) {
        const normalized = suggestion.uid
          ? normalizeIde(suggestion.uid)
          : undefined;
        const match = normalized ? existing.get(normalized) : undefined;
        if (match && normalized && !seen.has(normalized)) {
          seen.add(normalized);
          found.push({
            kind: 'existing',
            key: `existing:${match.companyId}`,
            existing: { ...match, ide: suggestion.uid ?? match.ide },
          });
        } else {
          register.push({
            kind: 'register',
            key: `register:${suggestion.ehraid}`,
            suggestion,
          });
        }
      }
      return [...found, ...register];
    },
    [],
  );

  const runSearch = React.useCallback(
    async (query: string) => {
      const requestId = ++requestRef.current;
      setLoading(true);
      setMessageKey(null);
      setHasSearched(false);
      setOpen(true);

      // A UID typed straight in: Bill Alps knows it or not, no register
      // call needed — and it keeps working once the monthly quota is spent.
      const typedIde = detectExisting ? normalizeIde(query) : undefined;
      if (typedIde) {
        const existing = await lookupExisting([query]);
        if (requestRef.current !== requestId) return;
        const match = existing.get(typedIde);
        setOptions(
          match
            ? [
                {
                  kind: 'existing',
                  key: `existing:${match.companyId}`,
                  existing: match,
                },
              ]
            : [],
        );
        setHighlightedIndex(0);
        setHasSearched(true);
        setLoading(false);
        return;
      }

      try {
        const { results } = await searchCompanies({
          name: query,
          locale: locale as Locale,
        });
        if (requestRef.current !== requestId) return;
        const existing = await lookupExisting(
          results.map((r) => r.uid).filter((uid): uid is string => !!uid),
        );
        if (requestRef.current !== requestId) return;
        setOptions(buildOptions(results, existing));
        setHighlightedIndex(0);
        setHasSearched(true);
      } catch (err) {
        if (requestRef.current !== requestId) return;
        setOptions([]);
        setHasSearched(true);
        setMessageKey(zefixErrorKey(err) ?? 'zefix:errors.unavailable');
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    },
    [searchCompanies, locale, detectExisting, lookupExisting, buildOptions],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    onValueChange(next);
    // Any edit invalidates a pick: the summary disappears with it.
    onSelect(null);
    clearTimeout(debounceRef.current);
    requestRef.current += 1;
    setLoading(false);
    setOptions([]);
    setHasSearched(false);

    if (next.trim().length < MIN_QUERY_LENGTH) {
      setMessageKey(null);
      setOpen(false);
      return;
    }

    const typedIde = detectExisting && normalizeIde(next.trim()) !== undefined;
    if (outOfSearches && !typedIde) {
      // Stop scheduling entirely — the monthly budget is spent. The field stays
      // a plain text input, which is the whole point.
      setMessageKey('zefix:errors.rateLimited');
      setOpen(true);
      return;
    }

    setMessageKey(null);
    debounceRef.current = setTimeout(
      () => void runSearch(next.trim()),
      DEBOUNCE_MS,
    );
  };

  const selectOption = React.useCallback(
    async (option: SearchOption) => {
      clearTimeout(debounceRef.current);
      const requestId = ++requestRef.current;
      setOpen(false);
      setOptions([]);
      setHasSearched(false);
      setMessageKey(null);

      if (option.kind === 'existing') {
        // No detail fetch: the company is already on Bill Alps, the prefill
        // is the request target. Costs no register quota.
        const { existing } = option;
        onValueChange(existing.name);
        onSelect({
          name: existing.name,
          ide: existing.ide,
          existingCompany: {
            companyId: existing.companyId,
            name: existing.name,
            owned: existing.owned,
          },
        });
        return;
      }

      const { suggestion } = option;
      onValueChange(suggestion.name);
      setLoading(true);
      try {
        const prefill = await getCompanyDetails({ ehraid: suggestion.ehraid });
        if (requestRef.current !== requestId) return;
        onSelect(prefill);
      } catch (err) {
        if (requestRef.current !== requestId) return;
        // The name is already in the field; only the extra fields are lost.
        onSelect(null);
        setMessageKey(zefixErrorKey(err) ?? 'zefix:errors.unavailable');
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    },
    [getCompanyDetails, onSelect, onValueChange],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (!open || options.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const option = options[highlightedIndex];
        if (option) void selectOption(option);
        break;
      }
    }
  };

  // A single muted row replaces the list whenever there is nothing to pick.
  const emptyRow = loading
    ? t('zefix:search.searching')
    : messageKey
      ? t(messageKey)
      : hasSearched && options.length === 0
        ? t('zefix:search.noResults')
        : null;

  const existingCount = options.filter((o) => o.kind === 'existing').length;
  const showGroups =
    detectExisting && existingCount > 0 && existingCount < options.length;

  const renderOption = (option: SearchOption, index: number) => {
    const secondary =
      option.kind === 'existing'
        ? option.existing.ide
        : [
            option.suggestion.legalSeat,
            option.suggestion.legalForm,
            option.suggestion.uid,
          ]
            .filter(Boolean)
            .join(' · ');
    const name =
      option.kind === 'existing'
        ? option.existing.name
        : option.suggestion.name;
    return (
      <li
        key={option.key}
        id={`${listboxId}-option-${index}`}
        role="option"
        aria-selected={index === highlightedIndex}
        data-option-index={index}
        data-option-kind={option.kind}
        onMouseEnter={() => setHighlightedIndex(index)}
        onClick={() => void selectOption(option)}
        className={cn(
          'flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 text-sm',
          index === highlightedIndex && 'bg-muted',
        )}
      >
        <Building2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="block truncate font-medium">{name}</span>
            {option.kind === 'existing' && (
              <Badge
                size="sm"
                variant={option.existing.owned ? 'outline-teal' : 'blue'}
              >
                {option.existing.owned
                  ? t('zefix:search.ownedBadge')
                  : t('zefix:search.existingBadge')}
              </Badge>
            )}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {secondary}
          </span>
        </span>
      </li>
    );
  };

  const groupHeading = (label: string) => (
    <li
      key={`heading:${label}`}
      role="presentation"
      className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
    >
      {label}
    </li>
  );

  const dropdown = (
    <div
      ref={dropdownRef}
      style={inlineResults ? INLINE_RESULTS_STYLE : dropdownStyle}
      className={cn(
        'animate-fade-in rounded-xl border-2 border-border bg-card shadow-hard',
        inlineResults && 'mt-2',
      )}
    >
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        className="max-h-60 overflow-y-auto p-1"
      >
        {options.length === 0 ? (
          <li className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
            {loading && <Loader2 className="size-4 animate-spin" />}
            {emptyRow}
          </li>
        ) : showGroups ? (
          <>
            {groupHeading(t('zefix:search.groupExisting'))}
            {options.slice(0, existingCount).map(renderOption)}
            {groupHeading(t('zefix:search.groupRegister'))}
            {options
              .slice(existingCount)
              .map((option, offset) =>
                renderOption(option, existingCount + offset),
              )}
          </>
        ) : (
          options.map(renderOption)
        )}
      </ul>
      <p className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
        {t('zefix:prefill.source')}
      </p>
    </div>
  );

  return (
    <div className={cn('space-y-1.5', className)}>
      <div ref={containerRef} className={cn(disablePortal && 'relative')}>
        <Input
          id={id}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          error={error}
          placeholder={placeholder ?? t('zefix:search.placeholder')}
          autoFocus={autoFocus}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={
            open && options.length > 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          startAdornment={<Building2 />}
          endAdornment={
            loading ? <Loader2 className="animate-spin" /> : undefined
          }
        />
        {open &&
          (disablePortal || inlineResults
            ? dropdown
            : createPortal(dropdown, document.body))}
      </div>
      {/* Muted, never destructive: not finding the company is a normal outcome. */}
      {!open && messageKey && (
        <p className="text-xs text-muted-foreground">{t(messageKey)}</p>
      )}
    </div>
  );
}
